import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from "react-redux";
import {compose} from "recompose";
import {
    convertDate,
    convertDateTo24HourString,
    sortCurrency,
    toCurrency
} from '../shared/functions';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import SalesLogDialog from './SalesLogDialog';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import CircularProgress from 'material-ui/CircularProgress';
import FlatButton from 'material-ui/FlatButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/action/receipt';
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import DetailTableIcon from 'material-ui/svg-icons/communication/location-on';
import Config from '../messages';

/**
 * Class for retrieving and displaying Sales Log Summary in a single table
 *
 */
class SalesLogReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            allowLocationSelection: true,
            detailComplete: false,
            detailList: [],
            detailTableData: [],
            detailTableHeaders: [
                {label: 'Id', key: 'sale_seq'},
                {label: 'Processed', key: 'sale_date_string'},
                {label: 'Subtotal', key: 'initial_net'},
                {label: 'Tax', key: 'initial_tax'},
                {label: 'Total', key: 'initial_total'},
                {label: 'Register', key: 'register'},
                {label: 'User', key: 'user'},
                {label: 'Mode', key: 'mode'},
                {label: 'Version', key: 'version'}
            ],
            detailLocation: {
                id: null,
                name: null,
                dba_name: null,
                selected: false,
                city: null,
                state: null
            },
            reportName: 'Sales Log',
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Location ID', key: 'location_id'},
                {label: 'Location Name', key: 'location_name'},
                {label: 'State', key: 'state'},
                {label: 'DBA', key: 'dba_name'}
            ],
            message: null,
            summaryComplete: false,
            selectedSequence: null,
        };
        this.detailPosition = null;
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.fetchData(null);
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.startTime !== this.props.startTime ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.endTime !== this.props.endTime ||
            nextProps.offset !== this.props.offset
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                detailList: [],
                detailTableData: [],
                detailComplete: false,
                detailLocation: {
                    id: null,
                    name: null,
                    dba_name: null,
                    selected: false,
                    city: null,
                    state: null
                },
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.startTime !== this.props.startTime ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.endTime !== this.props.endTime ||
            prevProps.offset !== this.props.offset
        ) {
            this.fetchData(null);
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get Sales data for a specific date range
     * @param locationId
     */
    fetchData(locationId) {
        // Assemble the URI
        const params = {
            start_date: this.props.startDate,
            start_time: this.props.startTime,
            end_date: this.props.endDate,
            end_time: this.props.endTime,
            offset: this.props.offset
        };

        let resource;
        if (locationId) {
            params.location_id = locationId;
            resource = reportsResources.SalesLogLocationByDate.GET_LIST(params);
        } else {
            resource = reportsResources.SalesLogByDate.GET_LIST(params);
        }

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(locationId, result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store Sales/type results
     *
     * @param locationId
     * @param data
     */
    processData(locationId, data) {

        const emptyCurrency = toCurrency(0);
        let summaryList = [];
        let detailList = [];

        if (data.aggregations) {
            if (!locationId) {
                if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                    let results = data.aggregations.by_location.buckets;
                    let totalResults = results.length;
                    if (totalResults) {
                        // Loop on the results
                        for (let s = 0; s < totalResults; s++) {
                            let locationResult = results[s];
                            let location = locationResult.column_name.hits.hits[0]._source.rxtxMsg.payload.location;
                            let dbaName = location.dba.name;
                            const locationRow = {
                                location_id: (location.id) ? location.id : '----',
                                location_name: (location.name) ? location.name : '----',
                                city: (location.city) ? location.city : '----',
                                state: (location.state) ? location.state : '(None)',
                                dba_name: (dbaName) ? dbaName : '(None)'
                            };
                            summaryList.push(locationRow);
                        }
                    }

                }
                // Finalize table data
                this.setState({
                    summaryList: summaryList,
                    summaryTableData: summaryList,
                    summaryComplete: true,
                });
                this.props.onSummaryComplete(true);
            }
            if (locationId) {
                if (data.aggregations.by_sale_seq.buckets) {
                    let results = data.aggregations.by_sale_seq.buckets;
                    let totalResults = results.length;
                    if (totalResults) {
                        for (let f = 0; f < totalResults; f++) {
                            let detailResult = results[f];
                            let details = detailResult.column_name.hits.hits[0]._source.rxtxMsg.payload;
                            let tDate = (details.payments[0].payment_date) ? details
                                .payments[0].payment_date : 'No Date';
                            let tDateString = (details.payments[0].payment_date) ? convertDate(details.payments[0].payment_date, 'display', true, true, this.props.offset, true) : 'No Date';
                            let tDate24HString = (details.payments[0].payment_date) ? convertDateTo24HourString(details.payments[0].payment_date, true, this.props.offset) : 'No Date';

                            const detailRow = {
                                sale_seq: (details.seq) ? details.seq : '----',
                                sale_date: tDate,
                                sale_date_string: tDateString,
                                sale_date_24h_string: tDate24HString,
                                initial_net: (details.initial_net) ? (toCurrency(details.initial_net / 100)) : emptyCurrency,
                                initial_tax: (details.initial_tax) ? (toCurrency(details.initial_tax / 100)) : emptyCurrency,
                                initial_total: (details.initial_total) ? (toCurrency(details.initial_total / 100)) : emptyCurrency,
                                register: (details.client.register) ? details.client.register : '---',
                                user: (details.client.user) ? details.client.user : '---',
                                mode: (details.mode) ? details.mode : '----',
                                version: (details.client.version) ? details.client.version : '---',
                            };
                            detailList.push(detailRow);
                        }
                    }
                }
                this.setState({
                        detailList: detailList,
                        detailTableData: detailList,
                        detailComplete: true,
                    },
                    () => {
                        this.scrollToDetailTable();
                    });
            }
        }
    }

    /* Table Building ------------------------------ */
    handleLocationSelect(event, row) {
        const location = {
            id: row.location_id,
            name: row.location_name,
            dba_name: row.dba_name,
            selected: true,
            city: row.city,
            state: row.state,
        };
        this.setState({
            allowLocationSelection: true,
            detailLocation: location,
        }, () => {
            this.scrollToDetailTable();
            this.fetchData(location.id);
        });
    }

    scrollToDetailTable() {
        window.scrollTo(0, this.detailPosition.offsetTop);
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    /**
     * Notify Sales Log Dialog of Sequence selection
     *
     * @param e
     * @param seq
     */
    handleSelectSequence = (e, seq) => {
        this.setState({
            selectedSequence: seq
        });
    };

    /**
     * Handle event from Dialog closure
     */
    handleDialogReset = () => {
        this.setState({
            selectedSequence: null
        });
    };

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /**
     * Convert Start and End Dates to range for Export filename
     *
     * @param startDate
     * @param endDate
     * @return {string}
     */
    convertDatesForFile(startDate, endDate) {
        let from = new Date(startDate + 'T00:00:00');
        let to = new Date(endDate + 'T00:00:00');
        let fileStr = '';
        let yy = from.getFullYear();
        let mo = String('00' + parseInt(from.getMonth() + 1, 10)).slice(-2);
        let dd = String('00' + from.getDate()).slice(-2);
        fileStr += yy.toString() + mo.toString() + dd.toString() + '-';
        yy = to.getFullYear();
        mo = String('00' + parseInt(to.getMonth() + 1, 10)).slice(-2);
        dd = String('00' + to.getDate()).slice(-2);
        fileStr += yy.toString() + mo.toString() + dd.toString();
        return fileStr;
    }

    /* Table Display ------------------------------ */

    render() {

        const {
            endDate,
            offset,
            startDate,
            system,
        } = this.props;

        const {
            allowLocationSelection,
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
            detailComplete,
            detailList,
            detailTableData,
            detailTableHeaders,
            detailLocation,
            selectedSequence
        } = this.state;

        const dateString = this.convertDatesForFile(startDate, endDate);
        const defaultSummarySort = setDefaultTableSort('location_id');
        const defaultDetailSort = setDefaultTableSort({field: 'sale_date', order: 'DESC'});

        // ReactTable headers and columns for Table
        const iconStyle = (allowLocationSelection) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;
        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Location Id',
                        accessor: 'location_id',
                        Cell: row => (
                            <span>
                                <span>{row.row.location_id}</span>
                                <DetailIcon
                                    onClick={(e) => this.handleLocationSelect(e, row.row)}
                                    style={iconStyle}
                                />
                            </span>
                        ),
                        style: tableStyles.right
                    },
                    {
                        Header: 'Location Name',
                        accessor: 'location_name',
                    },
                    {
                        Header: 'City',
                        accessor: 'city',
                        style: tableStyles.hide,
                        headerStyle: tableStyles.hide,
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                    },
                    {
                        Header: 'DBA',
                        accessor: 'dba_name',
                    }
                ]
            }
        ];
        const tableDetailsColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Id',
                        accessor: 'sale_seq',
                        style: tableStyles.wrapText,
                        Cell: props => (
                            <a style={tableStyles.linkDefault}
                               onClick={(e) => this.handleSelectSequence(e, props.row.sale_seq)}
                            >
                                {props.row.sale_seq}
                            </a>
                        )
                    },
                    {
                        Header: 'Processed',
                        accessor: 'sale_date',
                        Cell: props => (
                            <span>{props.row.sale_date_string}</span>
                        )
                    },
                    {
                        Header: '',
                        accessor: 'sale_date_string',
                        style: tableStyles.hide,
                        headerStyle: tableStyles.hide
                    },
                    {
                        Header: 'Subtotal',
                        accessor: 'initial_net',
                        style: tableStyles.tdMax,
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false
                    },
                    {
                        Header: 'Tax',
                        accessor: 'initial_tax',
                        style: tableStyles.tdMax,
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false
                    },
                    {
                        Header: 'Total',
                        accessor: 'initial_total',
                        style: tableStyles.tdMax,
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false
                    },
                    {
                        Header: 'Register',
                        accessor: 'register',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'User',
                        accessor: 'user',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Mode',
                        accessor: 'mode',
                        style: tableStyles.tdMax,
                    },
                    {
                        Header: 'Version',
                        accessor: 'version',
                        style: tableStyles.tdMax,
                    },
                ]
            }
        ];

        // Assemble Location Detail information
        const locationCity = (detailLocation && detailLocation.city) ? detailLocation.city + ', ' : '';
        const locationState = (detailLocation && detailLocation.state) ? detailLocation.state + '.' : '';

        return (
            <div>
                {summaryComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Sales Log */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export Sales Log */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={summaryTableData}
                                    headers={summaryTableHeaders}
                                    filename={'Sales-Log-Report-' + system + '-' + dateString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export Sales Log
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <TableIcon style={commonStyles.summaryIcon}/>
                            <span>Sales Log</span>
                        </h3>
                    </div>

                    {/* Table: Sales Log */}
                    <ReactTable
                        key="table-Sales-log-report"
                        data={summaryList}
                        columns={tableColumns}
                        defaultPageSize={10}
                        showPagination={true}
                        defaultSorted={defaultSummarySort}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                        onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                        ref={(r) => this.reactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped"
                    />
                </div>
                }
                {detailLocation.id && !detailComplete &&
                    <div style={commonStyles.loaderContainer}>
                        <h4>Generating Report...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            style={commonStyles.loader}
                        />
                    </div>
                }
                {detailComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Detail Sales Logs (Selected Location) */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export Detail Sales Logs (Selected Location) */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={detailTableData}
                                    headers={detailTableHeaders}
                                    filename={'Sales-Logs-Report-' + system + '-' + detailLocation.id + '-' + dateString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export Sales Logs Report {detailLocation.id}
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <DetailTableIcon style={commonStyles.summaryIcon}/>
                            <span>Sales Logs by Location {detailLocation.id}</span>
                        </h3>
                        <div style={commonStyles.tableDetail}>
                            Location Name: {detailLocation.name}<br/>
                            DBA: {detailLocation.dba_name}<br/>
                            {locationCity}{locationState}
                        </div>
                    </div>

                    {/* Table: Sales Logs (Selected Location) */}
                    <ReactTable
                        key="table-Sales-log-detail"
                        data={detailList}
                        columns={tableDetailsColumns}
                        defaultPageSize={10}
                        showPagination={true}
                        defaultSorted={defaultDetailSort}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortOrFilterChange(e, this.detailReactTable)}
                        onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.detailReactTable)}
                        ref={(r) => this.detailReactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped overflow no-print"
                    />
                    <ReactTable
                        key="table-Sales-log-detail-print"
                        data={detailTableData}
                        columns={tableDetailsColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        defaultSorted={defaultDetailSort}
                        defaultSortDesc={true}
                        filterable
                        resizable={false}
                        minRows={1}
                        className="print-only"
                    />

                    {/* Sales Log Dialog (triggered by Sequence ID click */}
                    <SalesLogDialog
                        sequence={selectedSequence}
                        offset={offset}
                        includeReceipt={true}
                        onCloseDialog={this.handleDialogReset}
                    />

                </div>
                }
                <div ref={(ref) => this.detailPosition = ref}>
                </div>
            </div>
        )
    }
}

SalesLogReport.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string,
    endTime: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    startTime: PropTypes.string,
    system: PropTypes.string,
};

SalesLogReport.defaultProps = {
    addField: true,
    endDate: null,
    endTime: null,
    offset: null,
    startDate: null,
    startTime: null,
};

const enhance = compose(
    connect()
);

export default enhance(SalesLogReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/SalesLogReport.js